/** The printable QR sheet of newly created invites (D14): one card per invite — its label, its
 *  QR code and its link — on a page of its own, printed with the browser's print dialog.
 *
 *  It opens in a new window (same origin, `about:blank`) rather than over the console: printing
 *  a page out of the app shell means fighting every fixed and scrolling container above it, and
 *  only the first page of a fixed dialog prints. The sheet is a static render — plain inline
 *  styles and inline SVG codes (`qrcode.react`), nothing fetched, no script — so the window
 *  inherits the console's CSP untouched (`style-src 'unsafe-inline'` is already granted). The
 *  links exist only in that window and in the result dialog's state. */
import { QRCodeSVG } from 'qrcode.react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

export interface QrSheetItem {
  label: string;
  publicId: string;
  link: string;
}

const SHEET_CSS = `
  @page { size: A4; margin: 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; color: #0a1a3f; }
  .sheet-head { padding: 12px 16px; border-bottom: 1px solid #d6dbe6; margin-bottom: 12px; }
  .sheet-head h1 { font-size: 18px; margin: 0 0 4px; }
  .sheet-head p { font-size: 12px; margin: 0; color: #5a6478; }
  .hint { background: #eef3fb; border: 1px solid #c9d6ee; border-radius: 6px; padding: 8px 12px; margin: 0 16px 12px; font-size: 13px; }
  .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8mm; padding: 0 16px 16px; }
  .card { border: 1px dashed #9aa4b8; border-radius: 6px; padding: 5mm; text-align: center; break-inside: avoid; page-break-inside: avoid; }
  .card h2 { font-size: 13px; margin: 0 0 3mm; overflow-wrap: anywhere; }
  .card svg { width: 42mm; height: 42mm; }
  .link { font-family: ui-monospace, Menlo, monospace; font-size: 8px; overflow-wrap: anywhere; margin: 3mm 0 0; color: #333; }
  .pid { font-size: 9px; color: #5a6478; margin: 1mm 0 0; }
  @media print { .hint { display: none; } .sheet-head { border-bottom: none; } }
`;

export function QrSheet({ title, items, createdAt }: { title: string; items: QrSheetItem[]; createdAt: Date }) {
  return (
    <>
      <header className="sheet-head">
        <h1>{title}</h1>
        <p>
          {items.length} invite link{items.length === 1 ? '' : 's'} · issued {createdAt.toLocaleString()} · each link
          works until it is used
        </p>
      </header>
      <p className="hint">Print this page (Ctrl+P / ⌘P), then close the window.</p>
      <main className="grid">
        {items.map((item) => (
          <section className="card" key={item.publicId} data-qr-card>
            <h2>{item.label}</h2>
            <QRCodeSVG value={item.link} size={256} marginSize={2} title={`Invite ${item.publicId}`} />
            <p className="link">{item.link}</p>
            <p className="pid">{item.publicId}</p>
          </section>
        ))}
      </main>
    </>
  );
}

/** Open the sheet in a new window and start printing. False when the browser blocked the
 *  window (the caller says so); call it straight from the click handler. */
export function printQrSheet(items: QrSheetItem[], title: string): boolean {
  const win = window.open('', '_blank');
  if (!win) return false;
  const doc = win.document;
  doc.title = title;
  const style = doc.createElement('style');
  style.textContent = SHEET_CSS;
  doc.head.appendChild(style);
  const mount = doc.createElement('div');
  doc.body.appendChild(mount);
  const root = createRoot(mount);
  // Render synchronously, so the codes are on the page before the print dialog snapshots it.
  flushSync(() => root.render(<QrSheet title={title} items={items} createdAt={new Date()} />));
  win.addEventListener('pagehide', () => root.unmount());
  win.focus();
  win.print();
  return true;
}
